import type { TableItem } from "~/interface/TableItem"
import type { TableBilling } from "~/interface/TableBilling"
import type { BillingStrategy, BillingOpts } from "./BillingStrategy"
import { discountLineFn } from "./discountLine"
import { roundMoney } from "~/utils/money"

/**
 * The standard Niceloop billing pipeline:
 *   subtotal → service charge → whole-bill discount → card charge → VAT → rounding.
 * This is the behaviour every shop gets unless it opts into another strategy.
 * (Body moved verbatim from the original TableHelper.calculateBilling.)
 *
 * ROUNDING: every intermediate is snapped to 2 decimals with roundMoney, matching
 * TableHelper.calculateItems (which rounds each per-line value). This is NOT
 * cosmetic — it is the v2.4.28 fix. Dropping it lets float noise reach `rounding`,
 * which is computed as a PLUG (final − beforeRounding); an unrounded residual
 * there breaks the receipt invariant. Keep this in sync with any new strategy.
 */
export const DefaultBillingStrategy: BillingStrategy = {
    key: "default",

    // usesSkipService intentionally omitted: nothing below reads item.skipService,
    // so callers skip resolving that tag entirely for every shop on this strategy.

    calculateBilling(items: TableItem[], globalOptions: any, tableOptions: any, opts?: BillingOpts): TableBilling {
        let sum = 0

        // ในมบเสร็จ SubTotal คำนวนจาก  extendedPrice,
        // ถูกคำนวณครับ ตอนสร้างใบเสร็จ Payment ที่ Class Receive Order Class ใน act function Apply Billing
        // TODO:  ต้องมาแก้ไขค่านี้ใหม่ให้มันเหมือนกันทั้งอันในระบบ ตอนนี้มันสับสน ในฟังก์ชัน Run เป็นแบบนี้ แต่ในฟังก์ชันจ่ายเงินถูกคำนวณทับอีกแบบหนึ่ง
        // Both are sums of already-2dp per-line values (see calculateItems), so they
        // are exact at 2 decimals; roundMoney only strips float noise from the sum.
        const subTotal = roundMoney(items.reduce((acc, item) => {
            return acc + item.discountedPrice;
            // return acc + item.extendedPrice;
        }, 0));
        sum += subTotal

        const itemsDiscount = roundMoney(items.reduce((acc, item) => {
            return acc + item.dc1[2];
        }, 0));

        //service charge
        // togoNoService: บิล TO-GO ไม่คิดค่าบริการ -> บังคับ sc = 0 ตอนคิดบิล
        const _serviceChargeMode = opts?.noServiceCharge ? 0 : (tableOptions?.sc?.[0] ?? globalOptions?.sc?.[0] ?? 0)
        const _serviceChargeValue = opts?.noServiceCharge ? 0 : (tableOptions?.sc?.[1] ?? globalOptions?.sc?.[1] ?? 0);
        const serviceChargeAmount = roundMoney(discountLineFn(sum, _serviceChargeMode, _serviceChargeValue))
        sum = roundMoney(sum + serviceChargeAmount)


        //addtional discount
        // mode 0 = percent (whole bill), 1 = fixed amount, 2 = percent EXCLUDING
        // no-discount-tagged lines AND lines that already carry a per-item discount.
        // Legacy parity (Food/model.base.js discountCalculate): the exclusion is done
        // by subtraction — sum the excluded lines' price out of the base, then apply %
        // to the remainder (service charge stays in the base, as in legacy).
        const _additionalDiscountMode = tableOptions?.dc2?.[0] ?? globalOptions?.dc2?.[0] ?? 0
        const _additionalDiscountValue = tableOptions?.dc2?.[1] ?? globalOptions?.dc2?.[1] ?? 0;
        let additionalDiscountAmount = 0;
        if (_additionalDiscountMode === 2) {
            if (_additionalDiscountValue > 0) {
                const excludedSubtotal = roundMoney(items.reduce((acc, item) => {
                    const isExcluded = item.noDiscount === true || (item.dc1?.[2] || 0) !== 0
                    return isExcluded ? acc + item.discountedPrice : acc
                }, 0))
                const eligibleBase = roundMoney(sum - excludedSubtotal)
                additionalDiscountAmount = roundMoney(eligibleBase * _additionalDiscountValue / 100)
            }
        } else {
            additionalDiscountAmount = roundMoney(discountLineFn(sum, _additionalDiscountMode, _additionalDiscountValue))
        }
        sum = roundMoney(sum - additionalDiscountAmount)

        //card charge
        const _cardChargeMode = tableOptions?.cc?.[0] ?? globalOptions?.cc?.[0] ?? 0
        const _cardChargeValue = tableOptions?.cc?.[1] ?? globalOptions?.cc?.[1] ?? 0;
        const cardChargeAmount = roundMoney(discountLineFn(sum, _cardChargeMode, _cardChargeValue))
        sum = roundMoney(sum + cardChargeAmount)


        //vat , 0 = none, 1 = inclusive, 2 = exclusive
        const _vatMode = tableOptions?.vat?.[0] ?? globalOptions?.vat?.[0] ?? 0
        const _vatValue = tableOptions?.vat?.[1] ?? globalOptions?.vat?.[1] ?? 0;
        let vatAmount = 0;

        if (_vatMode === 1) {
            // Inclusive VAT - VAT is already included in the price.
            // Informational only: it is NOT added to `sum`, and its rounding
            // residual must never leak into `rounding` (that would blow the plug up
            // to roughly -VAT).
            vatAmount = roundMoney((sum * _vatValue) / (100 + _vatValue));
        } else if (_vatMode === 2) {
            // Exclusive VAT - VAT is added on top
            vatAmount = roundMoney((sum * _vatValue) / 100);
            sum = roundMoney(sum + vatAmount);
        }
        // mode 0 = none, no VAT calculation


        //rounding ,  5/4, up ,down , none
        const roundingMode = tableOptions?.roundingMode ?? globalOptions?.roundingMode ?? 'none';
        let roundedSum = sum;
        let roundingAmount = 0;

        switch (roundingMode) {
            case '5/4':
                roundedSum = Math.round(sum);
                break;
            case 'up':
                roundedSum = Math.ceil(sum);
                break;
            case 'down':
                roundedSum = Math.floor(sum);
                break;
            case 'none':
            default:
                roundedSum = sum;
                break;
        }

        // `rounding` is the PLUG: it is always derived as (final - beforeRounding),
        // never guessed, so the receipt invariant holds by construction whatever the
        // shop's rounding rule is.
        roundingAmount = roundMoney(roundedSum - sum);
        sum = roundMoney(roundedSum);

        const grandTotal = sum;


        return {
            grandTotal,
            rounding: roundingAmount,
            roundingMode,

            sc: [_serviceChargeMode, _serviceChargeValue, serviceChargeAmount],
            dc2: [_additionalDiscountMode, _additionalDiscountValue, additionalDiscountAmount],
            cc: [_cardChargeMode, _cardChargeValue, cardChargeAmount],
            vat: [_vatMode, _vatValue, vatAmount],
            subTotal: subTotal,
            dc1: itemsDiscount,
        }
    }
}
